// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC } from 'react'
import { Input, Label } from '@/components/ui'
import { cn } from '@/lib/utils'

export interface IdMapSectionProps {
  title: string
  sources: string[]
  values: Record<string, string>
  onChange: (next: Record<string, string>) => void
  idPrefix: string
  placeholderHint?: string
  errors?: Record<string, string>
}

/** Source-ID → target-ID remapping grid with per-row inline conflict errors. */
export const IdMapSection: FC<IdMapSectionProps> = ({
  title, sources, values, onChange, idPrefix, placeholderHint, errors,
}) => (
  <div className="space-y-2">
    <Label className="text-sm font-semibold">{title}</Label>
    <div className="space-y-2 rounded-md border border-border p-3">
      {sources.map(source => {
        const error = errors?.[source]
        return (
          <div key={source} className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
            <span className="font-mono text-xs text-muted-foreground truncate pt-2" title={source}>
              {source}
            </span>
            <span className="text-muted-foreground text-xs pt-2">→</span>
            <div className="space-y-1 min-w-0">
              <Input
                id={`${idPrefix}-${source}`}
                value={values[source] ?? ''}
                onChange={(e) => onChange({ ...values, [source]: e.target.value })}
                placeholder={placeholderHint ?? 'new id'}
                className={cn('h-8 text-sm', error && 'border-destructive')}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>
          </div>
        )
      })}
    </div>
  </div>
)
